import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CommentIcon from '@mui/icons-material/Comment';
import EmailIcon from '@mui/icons-material/Email';
import HelpIcon from '@mui/icons-material/Help';
import BarChartIcon from '@mui/icons-material/BarChart';
import { ContextMenuAction } from './types';

const StyledContextMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '300px',
    boxShadow: '0px 2px 10px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    marginTop: '5px',
    [theme.breakpoints.down('sm')]: {
      width: '180px',
    },
  },
}));

const StyledContextMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  fontFamily: "'Lato', sans-serif",
  fontSize: '14px',
  color: '#052c40',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '& .MuiListItemIcon-root': {
    minWidth: '36px',
    color: '#255e7c',
  },
  '& .MuiTypography-root': {
    fontSize: '14px',
    fontWeight: 500,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 12px',
    '& .MuiListItemIcon-root': {
      minWidth: '28px',
    },
    '& .MuiTypography-root': {
      fontSize: '12px',
    },
  }
}));

// Common icon styling to ensure 16px size
const getIconStyle = (isMobile: boolean) => ({ 
  fontSize: isMobile ? '14px' : '16px'
});

interface ContextMenuProps<T> {
  anchorPosition: {
    top: number;
    left: number;
  } | null;
  open: boolean;
  onClose: () => void;
  onAction: (action: ContextMenuAction) => void;
  disabledActions?: ContextMenuAction[];
  customActions?: Array<{
    action: ContextMenuAction;
    label: string;
    icon: React.ReactNode;
    isVisible?: (row: any) => boolean;
  }>;
  currentRow?: any;
  editModeOnly?: boolean;
  viewMode?: boolean;
  hideDefaultActions?: boolean;
}

function TableContextMenu<T>({
  anchorPosition,
  open,
  onClose,
  onAction,
  disabledActions = [],
  customActions = [],
  currentRow = null,
  editModeOnly = false,
  viewMode = false,
  hideDefaultActions = false,
}: ContextMenuProps<T>): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Adjust anchor position for small screens to prevent menu from going off-screen
  const adjustedPosition = useMemo(() => {
    if (!anchorPosition) return undefined;
    
    const { top, left } = anchorPosition;
    
    // On mobile, we need to check if the menu would go off the screen
    if (isMobile) {
      const windowWidth = window.innerWidth;
      const menuWidth = 180; // Width of menu on mobile
      
      // If menu would go off right edge, adjust position
      const adjustedLeft = left + menuWidth > windowWidth 
        ? windowWidth - menuWidth - 10 // 10px padding from edge
        : left;
      
      return { top, left: adjustedLeft };
    }
    
    return anchorPosition;
  }, [anchorPosition, isMobile]);

  const isActionDisabled = (action: ContextMenuAction): boolean => {
    return disabledActions.includes(action);
  };

  const handleAction = (action: ContextMenuAction) => {
    onAction(action);
    onClose();
  };

  // Find a custom action if it exists
  const findCustomAction = (action: ContextMenuAction) => {
    return customActions.find(ca => ca.action === action);
  };

  // Get actions based on mode - either only edit action or all actions
  const getGroupedActions = () => {
    // If hideDefaultActions is true or we're in view mode, don't show any default actions
    if (hideDefaultActions || viewMode) {
      return [];
    }
    
    // In edit mode, only show edit action by default (custom actions are rendered separately)
    if (editModeOnly) {
      return [
      ];
    }
    
    if (!isMobile) {
      // Return all actions for desktop
      return [
        { action: 'download', label: 'Download', icon: <CloudDownloadIcon sx={getIconStyle(isMobile)} /> },
        { action: 'accept', label: 'Accept', icon: <CheckIcon sx={getIconStyle(isMobile)} /> },
        { action: 'delete', label: 'Delete', icon: <DeleteIcon sx={getIconStyle(isMobile)} /> },
        { action: 'ViewSamples', label: 'View Samples', icon: <VisibilityIcon sx={getIconStyle(isMobile)} /> },
        { action: 'editSamples', label: 'Edit Samples', icon: <EditIcon sx={getIconStyle(isMobile)} /> },
        { action: 'no2Report', label: 'NO2 Report', icon: <BarChartIcon sx={getIconStyle(isMobile)} /> },
        { action: 'no3Report', label: 'NO3 Report', icon: <BarChartIcon sx={getIconStyle(isMobile)} /> },
        { action: 'comment', label: 'Comment', icon: <CommentIcon sx={getIconStyle(isMobile)} /> },
        { action: 'contact', label: 'Contact', icon: <EmailIcon sx={getIconStyle(isMobile)} /> },
        { action: 'more', label: 'More', icon: <HelpIcon sx={getIconStyle(isMobile)} /> },
      ];
    }
    
    // For mobile, group some actions
    return [
      { action: 'download', icon: <CloudDownloadIcon sx={getIconStyle(isMobile)} /> },
      { action: 'accept', icon: <CheckIcon sx={getIconStyle(isMobile)} /> },
      { action: 'delete', icon: <DeleteIcon sx={getIconStyle(isMobile)} /> },
      { action: 'ViewSamples', icon: <VisibilityIcon sx={getIconStyle(isMobile)} /> },
      { action: 'editSamples', icon: <EditIcon sx={getIconStyle(isMobile)} /> },
      // Group reports into one section on mobile
      { action: 'no2Report', icon: <BarChartIcon sx={getIconStyle(isMobile)} />, label: t('contextMenu.reports', 'REPORTS') },
      { action: 'comment', icon: <CommentIcon sx={getIconStyle(isMobile)} /> },
      { action: 'contact', icon: <EmailIcon sx={getIconStyle(isMobile)} /> },
      { action: 'more', icon: <HelpIcon sx={getIconStyle(isMobile)} /> },
    ];
  };

  return (
    <StyledContextMenu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={adjustedPosition || undefined}
      PaperProps={{
        style: {
          maxHeight: isMobile ? '70vh' : '90vh',
          overflowY: 'auto',
        },
      }}
    >
      {getGroupedActions().map((item) => (
        <StyledContextMenuItem 
          key={item.action}
          onClick={() => handleAction(item.action as ContextMenuAction)}
          disabled={isActionDisabled(item.action as ContextMenuAction)}
        >
          <ListItemIcon>
            {item.icon}
          </ListItemIcon>
          <ListItemText 
            primary={
              item.label || 
              t(`contextMenu.${item.action}`, 
                item.action === 'no2Report' && isMobile ? 'REPORTS' : 
                item.action.toUpperCase())
            } 
          />
        </StyledContextMenuItem>
      ))}

      {/* Render any custom actions that aren't in the default list */}
      {customActions
        .filter(ca => {
          // Check visibility condition if defined and we have a row
          if (ca.isVisible && currentRow) {
            if (!ca.isVisible(currentRow)) {
              return false; // Skip this action if not visible for current row
            }
          }
          
          if (hideDefaultActions || editModeOnly) {
            // When hiding default actions or in edit mode, show all custom actions
            return true;
          }
          // In regular mode, filter out actions that exist in the default list
          return !['download', 'accept', 'delete', 'viewDispatches', 'editSamples', 
                   'no2Report', 'no3Report', 'comment', 'contact', 'more'].includes(ca.action);
        })
        .map(ca => (
          <StyledContextMenuItem 
            key={ca.action}
            onClick={() => handleAction(ca.action)}
            disabled={isActionDisabled(ca.action)}
          >
            <ListItemIcon>
              {React.isValidElement(ca.icon) 
                ? React.cloneElement(ca.icon as React.ReactElement, { 
                    sx: getIconStyle(isMobile) 
                  })
                : ca.icon
              }
            </ListItemIcon>
            <ListItemText primary={ca.label} />
          </StyledContextMenuItem>
        ))
      }
    </StyledContextMenu>
  );
}

export default TableContextMenu; 